// src/pages/portal/PortalLayout.tsx
//
// Layout for the customer portal (Day 5). Sidebar nav + top banner
// showing the customer's pricing tier. Items are gated: the route
// returns a <Navigate> to /login when the user is not signed in.

import { NavLink, Navigate, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  LogOut,
  UserCircle,
  Receipt,
  Wrench,
  Bell,
  Heart,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/components/auth/AuthContext";
import { usePortal } from "@/lib/portal/PortalContext";
import { useCart } from "@/lib/portal/cartStore";
import { BenchmarkWatermark } from "@/components/layout/BenchmarkWatermark";
import { cn } from "@/lib/cn";
import { useInvoicesText } from "@/lib/portal/invoicesI18n";

/**
 * i18n key per pricing tier. The tier NAME is a business label shown to the
 * customer, so it is translated; the tier KEY stays a stable machine value.
 */
const TIER_LABEL_KEY: Record<string, string> = {
  standard: "portal.tier.standard",
  preferred: "portal.tier.preferred",
  vip: "portal.tier.vip",
  quote: "portal.tier.quote",
};

const TIER_ACCENT: Record<string, string> = {
  standard: "bg-bg-elevated text-fg-secondary",
  preferred: "bg-accent-indigo/15 text-accent-indigo",
  vip: "bg-accent-amber/15 text-accent-amber",
  quote: "bg-bg-elevated text-fg-tertiary",
};

export function PortalLayout() {
  const auth = useAuth();
  const portal = usePortal();
  const cart = useCart();
  const invoicesText = useInvoicesText();
  const { t } = useTranslation();
  const navItems = [
    { to: "/portal", label: t("portal.nav.dashboard"), icon: LayoutDashboard, exact: true },
    { to: "/portal/catalog", label: t("portal.nav.catalog"), icon: Package },
    { to: "/portal/favorites", label: t("favorites.nav", "Favorites"), icon: Heart },
    { to: "/portal/cart", label: t("portal.nav.cart"), icon: ShoppingCart, badge: "cart" as const },
    { to: "/portal/orders", label: t("portal.nav.orders"), icon: Receipt },
    { to: "/portal/invoices", label: invoicesText.title, icon: Receipt },
    { to: "/portal/repairs", label: t("portal.nav.repairs"), icon: Wrench },
  ];

  if (!auth.isReady || portal.loading) {
    return (
      <div className="container py-12 text-center text-sm text-fg-tertiary">
        {t("portal.loadingAccount")}
      </div>
    );
  }
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (!portal.customer) {
    // Authenticated but no customer is linked to this identity
    // (customer_identities has no row for the kc sub). Fail closed —
    // never fall back to another customer.
    return (
      <div className="container py-16 text-center">
        <h1 className="text-xl font-510 text-fg-primary">{t("portal.noAccountLinked.title")}</h1>
        <p className="mt-2 text-sm text-fg-tertiary">
          {t("portal.noAccountLinked.body")}
        </p>
      </div>
    );
  }

  const customer = portal.customer;
  const tierLabel = t(TIER_LABEL_KEY[customer.pricingTier] ?? "portal.tier.standard");
  const tierAccent = TIER_ACCENT[customer.pricingTier] ?? "bg-bg-elevated text-fg-secondary";

  return (
    <>
      {/* Behind sidebar + main grid — slightly stronger so it reads
          through the dense panels. */}
      <BenchmarkWatermark intensity="subtle" />
      <div className="relative z-10 container grid gap-6 py-8 lg:grid-cols-[220px_1fr]">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-20 lg:h-[calc(100vh-6rem)]">
          <div className="mb-4 rounded-md border border-border bg-bg-panel p-3">
            <div className="flex items-center gap-2">
              <UserCircle className="h-4 w-4 text-fg-secondary" strokeWidth={1.5} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-510 text-fg-primary">
                  {customer.contactName}
                </div>
                <div className="truncate text-[10px] text-fg-quaternary">{customer.email}</div>
              </div>
            </div>
            <div className="mt-3">
              <span className={cn("rounded px-2 py-0.5 text-[10px] font-510 uppercase tracking-wider", tierAccent)}>
                {tierLabel}
              </span>
            </div>
            <div className="mt-2 text-[10px] text-fg-tertiary">
              {customer.companyName} - {customer.country}
            </div>
          </div>

          <nav className="space-y-0.5">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/portal" || (item as { exact?: boolean }).exact === true}
                className={({ isActive }) =>
                  cn(
                    "flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors",
                    isActive
                      ? "bg-benchmark-teal/15 text-benchmark-teal-bright"
                      : "text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary",
                  )
                }
              >
                <span className="flex items-center gap-2">
                  <item.icon className="h-4 w-4" strokeWidth={1.5} />
                  {item.label}
                </span>
                {item.badge === "cart" && cart.itemCount > 0 && (
                  <span className="rounded-full bg-benchmark-teal/30 px-1.5 text-[10px] text-benchmark-teal-bright">
                    {cart.itemCount}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="mt-6">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => auth.logout()}
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t("nav.logout")}
            </Button>
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0 space-y-6">
          <header className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-bg-panel p-4">
            <div>
              <h1 className="text-xl font-510 tracking-tight text-fg-primary">
                {customer.companyName}
              </h1>
              <p className="mt-0.5 text-xs text-fg-tertiary">
                {t("portal.customerId")}{" "}
                <code className="font-mono">{customer.id}</code>
                {customer.activeSince && (
                  <>
                    {" - "}
                    {t("portal.activeSince")}{" "}
                    {new Date(customer.activeSince).toLocaleDateString()}
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={cn("rounded px-2 py-1 text-xs font-510 uppercase tracking-wider", tierAccent)}>
                {tierLabel}
              </span>
              <span className="rounded-full bg-bg-elevated p-2 text-fg-secondary">
                <Bell className="h-3.5 w-3.5" strokeWidth={1.5} />
              </span>
            </div>
          </header>

          <Outlet />
        </main>
      </div>
    </>
  );
}
